import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Button, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { historySummaryLanguage, useVehicleHistorySummary } from '@/hooks/useVehicleHistorySummary';

function SummaryText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <ButtonBase
      onClick={() => { setExpanded((value) => !value); }}
      aria-expanded={expanded}
      sx={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 1,
        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}
    >
      <Typography component="span" variant="body2" sx={{
        display: '-webkit-box', WebkitBoxOrient: 'vertical',
        WebkitLineClamp: expanded ? 'unset' : 6, overflow: 'hidden',
        whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
      }}>
        {text}
      </Typography>
    </ButtonBase>
  );
}

export function VehicleHistorySummary({ vehicleId }: { vehicleId?: string }) {
  const { t, i18n } = useTranslation('workorders');
  const language = historySummaryLanguage(i18n.resolvedLanguage ?? i18n.language);
  const { data, isFetching, isError, refetch } = useVehicleHistorySummary(vehicleId, language);
  const showLoading = Boolean(vehicleId) && (isFetching || (!isError && data === undefined));

  return (
    <Box component="section" aria-label={t('historySummary.title')} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        <AutoAwesomeIcon fontSize="small" color="primary" sx={{ mt: '2px' }} />
        <Typography variant="subtitle2">{t('historySummary.title')}</Typography>
      </Stack>
      {showLoading ? (
        <Stack direction="row" spacing={1} alignItems="center" role="status">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">{t('historySummary.loading')}</Typography>
        </Stack>
      ) : isError ? (
        <Alert
          severity="info"
          sx={{
            alignItems: 'flex-start',
            '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' },
            '& .MuiAlert-action': { alignItems: 'flex-start', pt: 0, pl: 0, ml: 0.5 },
          }}
          action={(
            <Button color="inherit" size="small" onClick={() => { void refetch(); }}>
              {t('historySummary.retry')}
            </Button>
          )}
        >
          {t('historySummary.unavailable')}
        </Alert>
      ) : (
        <Stack spacing={1.5} key={`${vehicleId ?? 'new'}-${language}`}>
          {data?.customerSummary ? (
            <SummaryText key={data.customerSummary} text={data.customerSummary} />
          ) : (
            <Typography variant="body2" color="text.secondary">{t('historySummary.empty')}</Typography>
          )}
          {data?.internalSummary && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ '& .MuiAlert-message': { width: '100%', minWidth: 0 } }}>
              <AlertTitle>{t('historySummary.internal')}</AlertTitle>
              <SummaryText key={data.internalSummary} text={data.internalSummary} />
            </Alert>
          )}
        </Stack>
      )}
    </Box>
  );
}
